import React, { useState } from 'react'

import { Container, LayerParsed } from './Layers.styles'

import { Layer, LayerId } from 'modules/shares/types'
import { InspectorState } from 'modules/shares/Inspector/types'

import {
  hasParentLayerWithTint,
  hasParentLayerWithExports,
  hasParentLayerWithOpacity,
} from './Layers.utils'
import { scaleFrame } from 'utils/math'
import { isMac } from 'utils'

// Layer types that shouldn't appear in the inspector
const SKIP_LAYER_TYPES = ['MSImmutableHotspotLayer']

/**
 * TYPES
 */
interface LayersProps {
  zoomFactor: number
  inspector: InspectorState
}

interface LayersParsedProps {
  layers?: Record<string, Layer>
  zoomFactor: number
  skippingGroups: boolean
}

/**
 * HELPER COMPONENTS
 */
const LayersParsed = ({
  layers = {},
  zoomFactor,
  skippingGroups,
}: LayersParsedProps) => (
  <>
    {Object.values(layers)
      // Scale the frame depending on the current zoom
      .map(({ frame, ...rest }) => ({
        frame: scaleFrame(zoomFactor, frame),
        ...rest,
      }))
      // Sort layers depending on the Layer `order` property
      .sort((a, b) => ((a.order as number) || 0) - ((b.order as number) || 0))
      // We want to avoid rendering some type of layers
      .filter(
        ({ isCombined, type }) =>
          !isCombined && !SKIP_LAYER_TYPES.includes(type)
      )
      // Generate an absolute positioned div for each selectable layer
      .map(layer => {
        const { id, name, frame, type } = layer

        // There are some situations where we want to make a layer unclickable:
        // - when the layer is a group and ⌘ key is pressed
        // - when a layer is a parent group layer that has a Tint (because
        //   we want to select the Tint layer instead)
        // - when a layer has a parent group layer that has exports (because
        //   we want to select the export layer instead)
        const isGroupAndCommandKeyIsPressed = skippingGroups && type === 'group'
        const isLayerWithParentTint =
          hasParentLayerWithTint(layers, layer) && type !== 'text'
        const isLayerWithParentExports =
          hasParentLayerWithExports(layers, layer) && type !== 'text'
        const isLayerWithParentOpacity =
          hasParentLayerWithOpacity(layers, layer) && type !== 'text'

        const isLayerUnclickable =
          isGroupAndCommandKeyIsPressed ||
          isLayerWithParentTint ||
          isLayerWithParentExports ||
          isLayerWithParentOpacity

        return (
          <LayerParsed
            key={id as string}
            isLayerUnclickable={isLayerUnclickable}
            data-name={name}
            data-id={id}
            data-testid={id}
            frame={frame}
          />
        )
      })}
  </>
)

const MemoizedLayersParsed = React.memo(LayersParsed)

/**
 * MAIN COMPONENT
 * Renders layers in the inspector canvas where a user can hover and click
 */
const Layers: React.FC<LayersProps> = ({
  zoomFactor,
  inspector: { onHoverLayer, onClickLayer, data: inspectorData },
  ...rest
}) => {
  const [skippingGroups, setSkippingGroups] = useState<boolean>(false)

  const layerIdFromTarget = (target: HTMLElement) =>
    target?.dataset?.id as LayerId

  // Holding ⌘ (mac) or ⌃ (win) while the user is moving around the inspector
  // will make the groups unclickable
  const handleMouseMove = (event: React.MouseEvent): void => {
    const isCommandKeyPressed = isMac() ? event.metaKey : event.ctrlKey

    if (isCommandKeyPressed !== skippingGroups) {
      setSkippingGroups(isCommandKeyPressed)
    }

    onHoverLayer(layerIdFromTarget(event.target as HTMLElement))
  }

  const handleMouseLeave = (): void => {
    onHoverLayer(undefined)
  }

  const handleTouchMove = (event: React.TouchEvent): void => {
    const myLocation = event.changedTouches[0]
    const realTarget = document.elementFromPoint(
      myLocation.clientX,
      myLocation.clientY
    ) as HTMLElement

    onHoverLayer(layerIdFromTarget(realTarget))
  }

  const handleTouchEnd = (): void => {
    onHoverLayer(undefined)
  }

  const handleClick = (event: React.MouseEvent): void => {
    event.stopPropagation()
    onClickLayer?.(layerIdFromTarget(event.target as HTMLElement))
  }

  return (
    <Container
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      {...rest}>
      <MemoizedLayersParsed
        layers={inspectorData?.layers}
        zoomFactor={zoomFactor}
        skippingGroups={skippingGroups}
      />
    </Container>
  )
}

export default Layers
